import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, Injector } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { filter, firstValueFrom, Observable, shareReplay, switchMap } from 'rxjs';

import { User } from '@23degrees/shared-code/models';
import { ExternalLoadService } from '@23degrees/shared/services/external';
import { ANALYTICS_INIT_SCRIPT } from './init-script';

declare const gtag: any;

declare global {
  interface Window {
    google: any;
  }
}

@Injectable({ providedIn: 'root' })
export class GoogleService {
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private externalLoadService = inject(ExternalLoadService);
  private documentRef = inject(DOCUMENT);
  private windowRef = this.documentRef.defaultView;

  private tagId = 'GTM-K2NQW92M';

  private proxyUrls = ['google-analytics.com', 'googletagmanager.com'];

  private fetchPatched = false;

  private useProxy = false;

  private loaded = false;

  constructor(private injector: Injector) {
    // afterNextRender(() => {
    //   this.init();
    // });
  }

  public useFactory(): void {
    this.init();
  }

  public setupDefaults() {
    // gtag('js', new Date());
    // gtag(
    //   'consent',
    //   'default',
    //   {
    //     ad_personalization: 'denied', // 'granted',
    //     ad_storage: 'denied', // 'granted',
    //     ad_user_data: 'denied', // 'granted',
    //     analytics_storage: 'denied', // 'granted',
    //     functionality_storage: 'granted', // 'granted',
    //     personalization_storage: 'granted', // 'granted',
    //     security_storage: 'granted', // 'granted',
    //   },
    // );
  }

  private async init() {
    if(this.loaded) return;
    await firstValueFrom(this.externalLoadService.loadScript('ANALYTICS_INIT_SCRIPT', ANALYTICS_INIT_SCRIPT()));

    this.patchFetch();

    this.pushToDataLayer({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
      page_referral_url: document.referrer,
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        switchMap(() => this.addTagManagerToDom()),
        filter((loaded) => !!loaded),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const gtmTag = {
          event: 'page',
          pageName: this.router.url,
        };
        this.pushToDataLayer(gtmTag);
      });
    
    this.loaded = true;
  }

  private patchFetch(): void {
    if (!this.useProxy || this.fetchPatched) return;

    // store the original fetch function
    const origFetch = this.windowRef.fetch;

    const proxyFetch = async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
      const link = typeof input === 'string' ? input : input.url;
      if (this.proxyUrls.some((proxy) => link.includes(proxy))) {
        const proxyInit = { body: JSON.stringify({ input, init }), headers: { 'content-type': 'application/json' }, method: 'POST' };
        return origFetch('/api/v3/tunnel/google', proxyInit);
      }

      return origFetch(input, init);
    };

    // replace the fetch function with the proxyFetch
    this.windowRef.fetch = proxyFetch;
    this.fetchPatched = true;
  }

  public getDataLayer(): any[] {
    const win: any = this.windowRef;
    win.dataLayer = win.dataLayer || [];
    return win.dataLayer;
  }

  public pushToDataLayer(obj: object): void {
    const dataLayer = this.getDataLayer();
    dataLayer.push(obj);
  }

  trackEvent(event: string, properties: { [key: string]: string | number | boolean | string[] | number[] } = {}): void {
    this.pushToDataLayer({
      event,
      ...properties,
    });
  }

  public setUser(event: string, user: User) {
    this.pushToDataLayer({
      event,

      userId: user._id,
      userName: user.name,
      userRole: 'free',
      userEmail: user.email.replace('@', '$'),
      userTheme: user.userPreferences.theme,
      userLanguage: user.userPreferences.lang,
    });
  }
  // public gtag(...args){
  //   const win: any = this.windowRef;
  //   if(!win.gtag){
  //     win.gtag = (...args) => {this.pushToDataLayer(args)};
  //   }
  //   win.gtag(args);

  // }

  public addTagManagerToDom(): Observable<boolean> {
    const scriptSrc = `https://www.googletagmanager.com/gtm.js?id=${this.tagId}`;
    const proxySrc = `/api/v3/tunnel/google/${btoa(scriptSrc)}`;

    // return this.externalLoadService.loadScript('GTMscript', scriptText).pipe(shareReplay());
    return this.externalLoadService.loadScriptByUrl(this.useProxy ? proxySrc : scriptSrc).pipe(shareReplay());
  }

  public async oneTap(
    { client_id, auto_select = false, cancel_on_tap_outside = false, context = 'signin', ...otherOptions },
    callback: (response: { credential: string; select_by: string }) => void,
  ) {
    const contextValue = ['signin', 'signup', 'use'].includes(context) ? context : 'signin';

    await firstValueFrom(this.externalLoadService.loadScriptByUrl('https://accounts.google.com/gsi/client').pipe(shareReplay()));
    // https://developers.google.com/identity/gsi/web/reference/js-reference
    if (window.google) {
      window.google.accounts.id.initialize({
        client_id: client_id,
        callback: callback,
        auto_select: auto_select,
        cancel_on_tap_outside: cancel_on_tap_outside,
        context: contextValue,
        ...otherOptions,
      });
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // continue with another identity provider.
        }
      });
    }
  }
}
